import * as Sentry from '@sentry/react'
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Loader2, RefreshCw, WifiOff, Settings as SettingsIcon } from 'lucide-react'
import { ProviderAuthGate } from '@/components/shared/ProviderAuthGate'
import { useAuth } from '@/hooks/useAuth'
import { usePermissions } from '@/hooks/usePermissions'
import { useConnectionStatus, type ConnectionState } from '@/hooks/useConnectionStatus'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { OPEN_PROVIDER_EVENT, useProviderTapTrigger } from '@/hooks/useProviderTrigger'
import { Layout } from '@/components/layout/Layout'
import { Login } from '@/pages/Login'
import { Dashboard } from '@/pages/Dashboard'
import { MemberList } from '@/pages/members/MemberList'
import { MemberForm } from '@/pages/members/MemberForm'
import { MemberDetail } from '@/pages/members/MemberDetail'
import { MembershipList } from '@/pages/memberships/MembershipList'
import { PaymentList } from '@/pages/payments/PaymentList'
import { PaymentForm } from '@/pages/payments/PaymentForm'
import { EventList } from '@/pages/events/EventList'
import { DeviceList } from '@/pages/devices/DeviceList'
import { DeviceDetail } from '@/pages/devices/DeviceDetail'
import { ReportDashboard } from '@/pages/reports/ReportDashboard'
import { Settings } from '@/pages/settings/Settings'
import { SyncPage } from '@/pages/sync/SyncPage'
import { SystemConfig } from '@/pages/admin/SystemConfig'
import { SystemUsers } from '@/pages/admin/users'
import { PlanLimitModal } from '@/components/shared/PlanLimitModal'
import { Unauthorized } from '@/pages/Unauthorized'
import { IS_ELECTRON } from '@/lib/env'

const Router = IS_ELECTRON ? HashRouter : BrowserRouter

function BootSplash({
  state,
  error,
  refresh,
  appVersion,
  onConfigureDb,
}: {
  state: ConnectionState
  error: string | null
  refresh: () => void
  appVersion: string | null
  onConfigureDb?: () => void
}) {
  const isError = state === 'error' || state === 'noDB'
  const handleLogoTap = useProviderTapTrigger()

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden animate-[fade-in_0.25s_ease]"
      style={{ background: 'linear-gradient(135deg, #0c1c2e 0%, #0b2d1f 45%, #0e1a32 100%)' }}
    >
      {/* Decorative orbs */}
      <div
        className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, hsl(160 84% 39% / 0.35) 0%, transparent 70%)' }}
      />
      <div
        className="absolute bottom-0 -left-24 w-[400px] h-[400px] rounded-full blur-3xl pointer-events-none"
        style={{ background: 'radial-gradient(circle, #4f46e5 0%, transparent 70%)', opacity: 0.2 }}
      />

      {/* Logo */}
      <div
        onClick={handleLogoTap}
        className="w-14 h-14 bg-white/10 border border-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm mb-8 cursor-pointer select-none transition-transform active:scale-95"
        title="Veridia"
      >
        <span className="text-white font-bold text-xl tracking-tight">IS</span>
      </div>

      {isError ? (
        <div className="flex flex-col items-center gap-4 max-w-xs px-6 text-center">
          <div className="w-11 h-11 rounded-full bg-red-500/15 border border-red-500/25 flex items-center justify-center">
            <WifiOff className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <p className="text-white/80 text-sm font-semibold">
              {state === 'noDB' ? 'Sin base de datos' : 'No se pudo conectar'}
            </p>
            <p className="text-white/40 text-xs mt-1.5 leading-relaxed">
              {state === 'noDB'
                ? 'No hay una base de datos configurada. Configúrala para continuar.'
                : (error ?? 'El servidor no respondió a tiempo. Verifica la configuración.')}
            </p>
          </div>
          {state === 'noDB' && onConfigureDb ? (
            <button
              onClick={onConfigureDb}
              className="flex items-center gap-2 text-xs font-semibold text-white bg-emerald-600/80 border border-emerald-500/40 hover:bg-emerald-600 px-5 py-2.5 rounded-xl transition-all shadow-lg shadow-emerald-900/30"
            >
              <SettingsIcon className="w-3.5 h-3.5" />
              Configurar base de datos
            </button>
          ) : (
            <button
              onClick={refresh}
              className="flex items-center gap-2 text-xs font-medium text-white/60 bg-white/8 border border-white/12 hover:bg-white/12 hover:text-white/80 px-4 py-2 rounded-xl transition-all"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Reintentar
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-5 h-5 text-emerald-400 animate-spin" />
          <p className="text-white/40 text-sm">Iniciando sistema...</p>
        </div>
      )}

      {appVersion && (
        <span className="absolute bottom-6 text-white/20 text-xs font-mono">v{appVersion}</span>
      )}
    </div>
  )
}

function ProtectedRoute({
  element,
  permission,
  can,
}: {
  element: React.ReactElement
  permission?: string
  can: (p: string) => boolean
}) {
  if (permission && !can(permission)) {
    return <Unauthorized />
  }
  return element
}

export default function App() {
  const auth = useAuth()
  const { can } = usePermissions(auth.user)
  const connStatus = useConnectionStatus()
  const { appVersion } = useProxyConfig()
  const [showProviderSetup, setShowProviderSetup] = useState(false)

  // Global provider access: Ctrl+Shift+P / Cmd+Shift+P and custom event listener
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'P' || e.key === 'p' || e.code === 'KeyP')) {
        e.preventDefault()
        setShowProviderSetup(prev => !prev)
      }
    }

    function handleOpenProvider() {
      setShowProviderSetup(true)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener(OPEN_PROVIDER_EVENT, handleOpenProvider)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener(OPEN_PROVIDER_EVENT, handleOpenProvider)
    }
  }, [])

  // When state is noDB, render the ProviderAuthGate so the client can configure the DB.
  if (connStatus.state === 'noDB' || showProviderSetup) {
    return (
      <ProviderAuthGate
        onBack={() => {
          setShowProviderSetup(false)
          // Trigger a connection recheck after the provider exits
          connStatus.refresh()
        }}
      />
    )
  }

  // When the user has a stored token the app would render before the Electron backend
  // finishes initializing. Block rendering until the backend is ready to avoid 404s.
  if (auth.isAuthenticated && connStatus.state !== 'connected' && connStatus.state !== 'noProxy') {
    return (
      <BootSplash
        state={connStatus.state}
        error={connStatus.error}
        refresh={connStatus.refresh}
        appVersion={appVersion}
        onConfigureDb={() => setShowProviderSetup(true)}
      />
    )
  }

  if (!auth.isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<Login onLogin={auth.login} />} />
        </Routes>
      </Router>
    )
  }

  return (
    <Sentry.ErrorBoundary fallback={<div className="flex items-center justify-center h-screen text-text-muted text-sm">Algo salió mal. Recarga la página.</div>}>
      <Router>
        <Routes>
          <Route element={<Layout auth={auth} can={can} />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route
              path="/members"
              element={<ProtectedRoute element={<MemberList />} permission="users:read" can={can} />}
            />
            <Route
              path="/members/new"
              element={<ProtectedRoute element={<MemberForm />} permission="users:write" can={can} />}
            />
            <Route
              path="/members/:id"
              element={<ProtectedRoute element={<MemberDetail />} permission="users:read" can={can} />}
            />
            <Route
              path="/memberships"
              element={<ProtectedRoute element={<MembershipList />} permission="memberships:read" can={can} />}
            />
            <Route
              path="/payments"
              element={<ProtectedRoute element={<PaymentList />} permission="payments:read" can={can} />}
            />
            <Route
              path="/payments/new"
              element={<ProtectedRoute element={<PaymentForm />} permission="payments:write" can={can} />}
            />
            <Route
              path="/payments/cash-closings"
              element={<Navigate to="/payments" replace />}
            />
            <Route
              path="/events"
              element={<ProtectedRoute element={<EventList />} permission="events:read" can={can} />}
            />
            <Route
              path="/devices"
              element={<ProtectedRoute element={<DeviceList />} permission="devices:read" can={can} />}
            />
            <Route
              path="/devices/:id"
              element={<ProtectedRoute element={<DeviceDetail />} permission="devices:read" can={can} />}
            />
            <Route
              path="/reports"
              element={<ProtectedRoute element={<ReportDashboard />} permission="users:read" can={can} />}
            />
            <Route
              path="/sync"
              element={<ProtectedRoute element={<SyncPage />} permission="devices:read" can={can} />}
            />
            <Route
              path="/settings"
              element={<ProtectedRoute element={<Settings />} permission="users:*" can={can} />}
            />
            <Route
              path="/admin/config"
              element={<ProtectedRoute element={<SystemConfig />} permission="*" can={can} />}
            />
            <Route
              path="/admin/users"
              element={<ProtectedRoute element={<SystemUsers />} permission="users:write" can={can} />}
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </Router>
      <PlanLimitModal />
    </Sentry.ErrorBoundary>
  )
}
